import User from '../apps/default/User'

import Vue from 'vue'
import VueResource from 'vue-resource'
import moment from 'moment'
import * as _ from 'lodash'
import Utils from '@/providers/Utils'

Vue.use(VueResource)

/**
 * Interface to the Yii api
 */
export default {

    // main entry point for Yii2 api
    apiUrl: process.env.VUE_APP_API_HOST ? `${process.env.VUE_APP_API_HOST}/` : 'http://wcd-api.test/',

    inProduction: process.env.VUE_APP_API_HOST == 'https://api.wellcoaches.app',
    inDemo: process.env.VUE_APP_API_HOST == 'https://demo.api.wellcoaches.app',
    inLocalDev: process.env.VUE_APP_API_HOST === undefined,

    loadEnvironment() {
        return Vue.http.post(`${this.apiUrl}?r=site/environment`)
    },

    // --------- user functions ------------

    // (these mirror the corresponding functions in User.js, but we put them here to encapsulate
    // all actual http actions and references to apiUrl)

    checkAuth() {
        // if stored currentUser.auth_key is invalid, this will trigger a redirect to the login page
        // (see Utils.addAuthorizationInterceptor)
        return Vue.http.get(`${this.apiUrl}?r=user/check-auth`)
    },

    loginUser(vue, email, password) {
        let uuid = localStorage.getItem('uuid')
        let model = localStorage.getItem('model')
        return Vue.http.post(
            `${this.apiUrl}?r=user/login`,
            {email: email, password: password, uuid: uuid, model: model}
        )
    },

    logoutUser(vue, userId) {
        return Vue.http.post(
            `${this.apiUrl}?r=user/logout`,
            {id: userId}
        )
    },

    maskAsUser(userId) {
        return Vue.http.post(
            `${this.apiUrl}?r=user/mask`,
            {id: userId}
        )
    },

    switchToPractice(vue, practiceId) {
        return Vue.http.post(
            `${this.apiUrl}?r=user/switch-to-practice`,
            {practice_id: practiceId}
        )
    },

    switchToCompany(vue, companyId) {
        return Vue.http.post(
          `${this.apiUrl}?r=admin/switch-to-company`,
          {company_id: companyId}
        )
    },

    resetPassword(vue, email, password) {
        const urlBase = window.location.origin // e.g. 'http://localhost:8009'
        return Vue.http.post(
            `${this.apiUrl}?r=user/reset`,
            {email: email, password: password, url_base: urlBase}
        )
            .then((response) => {
                this.log(vue, 'user', null, 'reset',
                    `User reset password: ${email}`)
                return Promise.resolve(response)
            })
    },
    confirmPasswordReset(vue, email, confirm) {
        return Vue.http.post(
            `${this.apiUrl}?r=user/reset`,
            {email: email, confirm: confirm}
        )
            .then((response) => {
                this.log(vue, 'user', null, 'reset/confirm',
                    `User confirmed password reset: ${email}`)
                return Promise.resolve(response)
            })
        // NB: error is handled in UserResetPassword
    },

    // ---------- logging -----------

    // action is 'create', 'update', 'delete'
    log(vue, table, item, action, message = null) {
        const Table = _.capitalize(table)
        const id = (item && item.id ? `#${item.id}` : '')

        let text = ''
        if (item && ['create', 'update', 'delete'].indexOf(action) != -1) {
            let name = item.name
            if (table == 'participant') {
                name = item.firstname + ' ' + item.lastname
            } else if (table == 'provider') {
                name = item.type + ', participant ID: ' + item.participant_id + ', practice ID: ' + item.practice_id
            } else if (table == 'translation') {
                name = item.translation
            } else if (table == 'translation-source') {
                name = item.message
            } else if (!item.name) {
                name = ''
            }
            text = `${Table} ${id} ${action}d` + (name ? `: ${name}` : '')  // e.g. 'Participant #123 updated: Jane Doe'
        } else {
            text = message
        }

        const entry = {
            entry_type: `${table}/${action}`,
            user_id: vue && vue.$user ? vue.$user.userId() : null,
            text: text,
            logged: this.formatDate()
        }

        // NB: createItem has a check to avoid infinite loops!
        return this.createItem(this, 'user-log', entry)
    },

    getUserLogEntryTypes() {
        return Vue.http.get(`${this.apiUrl}?r=user-log/list-entry-types`)
            .then((response) => response.data)
    },

    // ---------- biometrics -----------

    importBiometrics(vue, dataRows, hasHeaderRow, comment) {
        return Vue.http.post(
            `${this.apiUrl}?r=biometrics/import`,
            {
                practiceId: vue.$user.practitionerPracticeId(),
                dataRows: dataRows,
                hasHeaderRow: hasHeaderRow,
                comment: comment
            }
        )
    },

    // ---------- practitioners/participants -----------

    getPractitioners(vue, filters, listParams) {
        return this.getResources(vue, 'practitioner', false, ['participant'], filters, listParams)
    },

    getProviders(vue, filters, listParams) {
        return this.getResources(vue, 'provider', false, ['practice'], filters, listParams)
    },

    findProvider(vue, practiceId, participantId) {
        const filters = {
            practice_id: {
                field: 'practice_id', value: practiceId
            },
            participant_id: {
                field: 'participant_id', value: participantId
            },
        }
        return this.getResources(vue, 'provider', true, [], filters)
            .then((response) => {
                return response
            })
    },

    getPatients(vue, filters, listParams, practiceId, expands) {
        filters.practice_id = {
            field: 'practice_id', value: practiceId
        }
        expands = ['participant', 'participant.surveyNotes'].concat(expands)
        return this.getResources(vue, 'provider', false, expands, filters, listParams)
    },

    getMessages (vue, filters, listParams, practiceId) {
        filters.practice_id = {
            field: 'practice_id', value: practiceId
        }
        return this.getResources(vue, 'message', false, [], filters, listParams)
    },

    async sendMessage (vue, subject, body, bccPractice, participantId, practiceId, attachmentIds, footerText) {
        const response = await Vue.http.post(`${this.apiUrl}?r=message/send`, {
            subject: subject,
            body: body,
            bcc_practice: bccPractice,
            participant_id: participantId,
            practice_id: practiceId,
            attachment_ids: attachmentIds,
            footer_text: footerText
        })
        this.log(vue, 'message', null, 'send',
            `Message "${subject}" sent to Participant #${participantId}`)
        return response
    },

    async sendNotes (vue) {
        const response = await Vue.http.post(`${this.apiUrl}?r=message/send-notes`)
        return response
    },


    getPlansForPractice (vue, practice) {
        const filters = {
            company_id: {
                field: 'company_id', value: practice.company_id
            }
        }
        return this.getResources(vue, 'plan', false, [], filters)
    },

    findPatients(vue, practiceId, searchText) {
        const filters = {
            practice_id: {
                field: 'practice_id', value: practiceId
            },
            text: {
                field: 'text', value: searchText
            }
        }
        return this.getResources(vue, 'provider', false, ['participant'], filters)
            .then((response) => {
                return Promise.resolve(_.map(response, (provider) => provider.participant))
            })
    },

    findPractitionerCodes(vue, practitionerRole, searchText) {
        const filters = {
            role: {
                field: 'role', value: practitionerRole
            },
            text: {
                field: 'display_name', value: searchText
            }
        }
        return this.getResources(vue, 'practitioner-code', false, [], filters)
    },

    findPracticeCodes(vue, searchText) {
        const filters = {
            text: {
                field: 'display_name', value: searchText
            }
        }
        return this.getResources(vue, 'practice-code', false, [], filters)
    },

    findPractices(vue, name, filters = {}) {
        filters.name = {
            // NB: must specify type=string to get partial matches
            field: 'practice.name', type: 'string', value: name
        }
        const listParams = {perPage: 100, currentPage: 0}
        return this.getResources(vue, 'practice', false, [], filters, listParams)
    },

    findParticipants(vue, email, expands = []) {
        const filters = {
            // NB: we do NOT get partial matches here
            email: {field: 'email', value: email}
        }
        const listParams = {perPage: 100, currentPage: 0}
        return this.getResources(vue, 'participant', false, expands, filters, listParams)
    },

    mergeParticipants(vue, donorId, recipientId, deleteDonor) {
        return Vue.http.post(`${this.apiUrl}?r=participant/merge`,
            {donorId: donorId, recipientId: recipientId, deleteDonor: deleteDonor}
        )
            .then((response) => {
                this.log(vue, 'participant', null, 'merge',
                    `Participant #${donorId} merged into #${recipientId}`)
                return Promise.resolve(response)
            })
    },

    cloneParticipant(vue, provider) {
        return Vue.http.post(`${this.apiUrl}?r=participant/clone`,
            {provider: provider}
        )
            .then((response) => {
                this.log(vue, 'participant', null, 'clone',
                    `Participant #${provider.participant_id} cloned into Practice #${provider.practice_id}`)
                return Promise.resolve(response)
            })
    },

    importPractices(vue, dataRows, hasHeaderRow, companyId) {
        return Vue.http.post(`${this.apiUrl}?r=practice/import`,
            {
                data_rows: dataRows,
                has_header_row: hasHeaderRow,
                company_id: companyId
            }
        )
            .then((response) => {
                this.log(vue, 'practice', null, 'import',
                    `Imported ${dataRows.length} practices`)
                return Promise.resolve(response)
            })
    },

    importParticipants(vue, dataRows, hasHeaderRow, comment, planId, practiceId) {
        return Vue.http.post(`${this.apiUrl}?r=participant/import`,
            {
                data_rows: dataRows,
                has_header_row: hasHeaderRow,
                comment: comment,
                plan_id: planId,
                practice_id: practiceId
            }
        )
            .then((response) => {
                this.log(vue, 'participant', null, 'import',
                    `Imported ${dataRows.length} participants`)
                return Promise.resolve(response)
            })
    },

    updateAppointment(vue, provider) {
        return Vue.http.post(
            `${this.apiUrl}?r=provider/update-appointment`,
            {provider: provider}
        )
            .then((response) => {
                this.log(vue, 'provider', null, 'update-appointment',
                    `Participant #${provider.participant_id} appointment updated`)
                return Promise.resolve(response)
            })
    },

    updateComment(vue, provider) {
        return Vue.http.post(
            `${this.apiUrl}?r=provider/update-comment`,
            {provider: provider}
        )
            .then((response) => {
                this.log(vue, 'provider', null, 'update-comment',
                    `Participant #${provider.participant_id} comment updated`)
                return Promise.resolve(response)
            })
    },

    createPlanResources(vue, planId) {
        return Vue.http.post(`${this.apiUrl}?r=plan/create-plan-resources`, {plan_id: planId})
            .then((response) => {
                this.log(vue, 'plan', null, 'create-plan-resources',
                    `Questionnaire Resources created/updated for Plan #${planId}`)
                return Promise.resolve(response)
            })
    },

    // ----------- surveys ---------------

    createSurvey(vue, questionnaireCode, participantId) {
        return Vue.http.post(`${this.apiUrl}?r=survey/create`,
            {
                questionnaire_code: questionnaireCode,
                participant_id: participantId
            }
        )
            .then((response) => {
                this.log(vue, 'survey', null, 'create',
                    `Survey created for Participant #${participantId}`)
                return Promise.resolve(response)
            })
    },

    createSurveys (vue, participantId, practiceId, questionnaireCodes) {
        return Vue.http.post(`${this.apiUrl}?r=survey/create-multiple`, {
            participant_id: participantId,
            questionnaireCodes: questionnaireCodes,
        })
            .then((response) => {
                this.log(vue, 'participant', null, 'createSurveys',
                    `Questionnaires ${questionnaireCodes.join(', ')} created for Participant #${participantId} by Practice #${practiceId}`)
                return Promise.resolve(response)
            })
    },

    createSurveyNote(vue, surveyNote) {
        return Vue.http.post(
            `${this.apiUrl}?r=survey-note/create`,
            surveyNote
        )
            .then((response) => {
                this.log(vue, 'survey-note', null, 'create',
                    `Survey Note created for Participant #${surveyNote.participant_id}`)
                return Promise.resolve(response.data)
            })
    },

    getParticipantScores(vue, participantId) {
        return Vue.http.get(
            `${this.apiUrl}?r=score/get-scores-for-participant&participant_id=${participantId}`
        )
            .then((response) => response.data)
    },

    getConiferScores(vue, participantId, surveyId) {
        return Vue.http.get(
            `${this.apiUrl}?r=score/get-conifer-scores&participant_id=${participantId}&survey_id=${surveyId}`
        )
            .then((response) => response.data)
    },

    downloadForm1500(vue, surveyNoteId) {
        return Vue.http.get(
            `${this.apiUrl}?r=survey-note/download-form-1500&id=${surveyNoteId}`,
            {responseType: 'arraybuffer'}
        )
            .then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/pdf'}))
                const link = document.createElement('a')
                link.href = url
                const preview = true // TEMP
                if (preview) {
                    link.setAttribute('target', '_blank')
                } else {
                    link.setAttribute('download', 'Form1500.pdf')
                }
                document.body.appendChild(link)
                link.click()
            })
    },

    getAggregateReportDataForCompany(companyId) {
        return Vue.http.get(
            `${this.apiUrl}?r=report/get-aggregate-report-data-for-company&company_id=${companyId}`
        )
            .then((response) => response.data)
    },

    getAggregateReportDataForPlan(planId) {
        return Vue.http.get(
            `${this.apiUrl}?r=report/get-aggregate-report-data-for-plan&plan_id=${planId}`
        )
            .then((response) => response.data)
    },

    getAggregateReportDataForPractice(practiceId) {
        return Vue.http.get(
            `${this.apiUrl}?r=report/get-aggregate-report-data-for-practice&practice_id=${practiceId}`
        )
            .then((response) => response.data)
    },

    getAggregateReportDataForPractitioner(practitionerId) {
        return Vue.http.get(
            `${this.apiUrl}?r=report/get-aggregate-report-data-for-practitioner&practitioner_id=${practitionerId}`
        )
            .then((response) => response.data)
    },

    // ------------------ DietId -----------------

    getDietIdWebhookUrl() {
        return `${this.apiUrl}?r=diet-id/webhook`
    },

    // ------------------ translations -----------------

    async loadTranslations (fileKey) {
        const response = await Vue.http.get(`/translations-${fileKey}.csv`)
        return response.data
    },

    // ----------- attachments -----------

    // NB: preview mode may not work in some browsers (??)
    async downloadAttachment (att, preview) {
        const response = await Vue.http.get(
            `${this.apiUrl}?r=attachment/download&id=${att.id}`,
            { responseType: 'arraybuffer' }
        )
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }))
        const link = document.createElement('a')
        link.href = url
        if (preview) {
            link.setAttribute('target', '_blank')
        } else {
            link.setAttribute('download', att.name)
        }
        document.body.appendChild(link)
        link.click()
        return response
    },

    // TODO: how to log uploads??
    getAttachmentUploadUrl (modelClass, modelId, group) {
        return `${this.apiUrl}?r=attachment/upload&model_class=${modelClass}&model_id=${modelId}&group=${group}`
    },

    // ----------- generic restful methods -----------

    async getItems(vue, table, filters, listParams, expands) {
        return this.getResources(vue, table, false, expands, filters, listParams)
    },

    async getAllItems(vue, table) {
        const filters = null
        const listParams = {perPage: 100000, currentPage: 0}
        return this.getResources(vue, table, false, [], filters, listParams)
    },

    async getItem(vue, table, id, expands = []) {
        const filters = {
            id: {field: `${table.replace('-', '_')}.id`, value: id}
        }
        return this.getResources(vue, table, true, expands, filters)
    },

    async createItem(vue, table, item) {
        const response = await Vue.http.post(
            `${this.apiUrl}?r=${table}/create`,
            item
        )
        if (table != 'user-log') { // avoid infinite loop when creating userlog entry!
            this.log(vue, table, item, 'create')
        }
        return response
    },

    async updateItem(vue, table, item, log = true) {
        const response = await Vue.http.post(
            `${this.apiUrl}?r=${table}/update&id=${item.id}`,
            item
        )
        if (log) {
            this.log(vue, table, item, 'update')
        }
        return response
    },

    async deleteItem(vue, table, item) {
        const response = await Vue.http.delete(
            `${this.apiUrl}?r=${table}/delete&id=${item.id}`
        )
        this.log(vue, table, item, 'delete')
        return response
    },

    // ------- wearables ---------

    getFitbitAuthUrl() {
        // origin is e.g. 'http://localhost:8009'
        const here = encodeURIComponent(window.location.origin)
        const requestUrl = `${this.apiUrl}?r=metrics/get-fitbit-auth-url&url_base=${here}`
        return Vue.http.get(requestUrl)
    },

    handleConnectToFitbitCode(participantId, code) {
        const here = encodeURIComponent(window.location.origin)
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/handle-connect-to-fitbit-code&url_base=${here}`,
            {participant_id: participantId, code: code}
        )
    },

    disconnectFromFitbit(participantId) {
        const here = encodeURIComponent(window.location.origin)
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/disconnect-from-fitbit&url_base=${here}`,
            {participant_id: participantId}
        )
    },

    refreshFitbitData(participantId) {
        const here = encodeURIComponent(window.location.origin)
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/refresh-fitbit-data&url_base=${here}`,
            {participant_id: participantId}
        )
    },

    getGoogleFitAuthUrl() {
        // origin is e.g. 'http://localhost:8009'
        const here = encodeURIComponent(window.location.origin)
        const requestUrl = `${this.apiUrl}?r=metrics/get-google-fit-auth-url&url_base=${here}`
        return Vue.http.get(requestUrl)
    },

    handleConnectToGoogleFitCode(participantId, code) {
        const here = window.location.origin
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/handle-connect-to-google-fit-code`,
            {participant_id: participantId, code: code, url_base: here}
        )
    },

    disconnectFromGoogleFit(participantId) {
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/disconnect-from-google-fit`,
            {participant_id: participantId}
        )
    },

    refreshGoogleFitData(participantId) {
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/refresh-google-fit-data`,
            {participant_id: participantId}
        )
    },

    postMetricManualEntry(participantId, entries) {
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/post-metric-manual-entry`,
            {participant_id: participantId, entries: entries}
        )
    },

    postMetricGoals(participantId, entries) {
        return Vue.http.post(
            `${this.apiUrl}?r=metrics/post-metric-goals`,
            {participant_id: participantId, entries: entries}
        )
    },

    // ------- emr ---------

    async emrScope(vue, params = {}) {
        try {
            const response = await Vue.http.get(`${this.apiUrl}?r=emr/scope`, {
                params: params
            })
            return response.body
        } catch (e) {
            if (vue) {
                let message = (e.body && e.body.message) ? e.body.message : 'Error checking EMR scope'
                Utils.handleError(e, message)
            }
        }
    },

    async emrCapabilities(vue, params = {}) {
        try {
            const response = await Vue.http.get(`${this.apiUrl}?r=emr/capabilities`, {
                params: params
            })
            return response.body
        } catch (e) {
            if (vue) {
                let message = (e.body && e.body.message) ? e.body.message : 'Error retrieving EMR capabilities'
                Utils.handleError(e, message)
            }
        }
    },

    async emrLookup(vue, filters, listParams, args) {
        let params = args ? args : {}
        if (listParams) {
            params.perPage = listParams.perPage
            params.page = listParams.currentPage - 1
            params.sort = (listParams.sortDesc ? '-' : '') + listParams.sortBy
        }
        if (filters) {
            params.filters = _.cloneDeep(filters)
            _.each(params.filters, (f) => {
                f.options = []
            })
        }

        try {
            const response = await Vue.http.get(`${this.apiUrl}?r=emr/lookup`, {
                params: params
            })
            if (listParams) {
                listParams.totalRows = response.body._meta.totalCount
            }
            return (response.body.items)
        } catch (e) {
            if (vue) {
                let message = (e.body && e.body.message) ? e.body.message : 'Error looking up data'
                Utils.handleError(e, message)
            }
        }
    },

    async emrSync(vue, filters, args) {
        let params = args ? args : {}
        if (filters) {
            params.filters = _.cloneDeep(filters)
            _.each(params.filters, (f) => {
                f.options = []
            })
        }

        try {
            const response = await Vue.http.get(`${this.apiUrl}?r=emr/sync`, {
                params: params
            })
            return response.body
        } catch (e) {
            if (vue) {
                let message = (e.body && e.body.message) ? e.body.message : 'Error synchronizing appointments'
                Utils.handleError(e, message)
            }
        }
    },

    async emrUpdate(vue, type, id) {
        let params = {
            type: type,
            id: id
        }
        try {
            const response = await Vue.http.get(`${this.apiUrl}?r=emr/update`, {
                params: params
            })
            return response.body
        } catch (e) {
            if (vue) {
                let message = (e.body && e.body.message) ? e.body.message : 'Error editing EMR record'
                Utils.handleError(e, message)
            }
        }
    },

    async emrDelete(vue, type, id, practiceId = null, companyId = null) {
        let params = {
            type: type,
            id: id,
        }
        if (practiceId) {
            params['practice'] = practiceId
        }
        if (companyId) {
            params['company'] = companyId
        }

        try {
            const response = await Vue.http.get(`${this.apiUrl}?r=emr/delete`, {
                params: params
            })
            return response.body
        } catch (e) {
            if (vue) {
                let message = (e.body && e.body.message) ? e.body.message : 'Error deleting EMR record'
                Utils.handleError(e, message)
            }
        }
    },

    // ------- appointments -------

    // get appointments for specified date range and user
    getCalendarAppointments(userId, dateStart, dateEnd) {
        return Vue.http.get(`${this.apiUrl}?r=appointment/populate-calendar` +
            `&user_id=${userId}` +
            `&date_range_start=${dateStart}` +
            `&date_range_end=${dateEnd}`
        ).then((response) => response.data)
    },

    // clone appointments from a selected date to a repeated pattern
    cloneAppointments(userId, origin, days, weeks) {
        return Vue.http.post(`${this.apiUrl}?r=appointment/clone-appointments`,
          {
              user_id: userId,
              origin: origin,
              days: days,
              weeks: weeks
          }
          ).then((response) => response.data)
    },

    /** returns ids of appointments in range of selected days and times
     * use to gather ids, confirm deletion of n appointments
     * then use clearAppointments api action to delete after confirmation */
    getAppointmentsToClear(userId, specDay, weekdays, weeks, timeRangeStart, timeRangeEnd, currentDate) {
        return Vue.http.get(`${this.apiUrl}?r=appointment/get-appointments-to-clear` +
            `&user_id=${userId}` +
            `&spec_day=${specDay ? specDay : ''}` +
            `&weekdays=${weekdays ? weekdays : ''}` +
            `&weeks=${weeks ? weeks : ''}` +
            `&time_range_start=${timeRangeStart}` +
            `&time_range_end=${timeRangeEnd}` +
            `&current_date=${currentDate ? currentDate: ''}`
          ).then((response) => response.data)
    },

    clearAppointments(appointments) {
        const apptIds = _.map(appointments, (appt) => {
            return appt.id
        })
        return Vue.http.post(`${this.apiUrl}?r=appointment/clear-appointments`,
          {
              appointments: apptIds
          }
        ).then((response) => response.data)
    },

    // ------- misc ---------

    // create an SQL datetime value for the current time (e.g. '2013-02-04 22:44:30')
    formatDate() {
        const d = moment().toISOString() // 2013-02-04T22:44:30.652Z
        return d.split('.')[0].replace('T', ' ')
    },

    // send a support request email
    sendSupportRequest(info, body) {
       return Vue.http.post(
           `${this.apiUrl}?r=support/send-support-email`,
           {info: info, body: body}
       )
    },

    getClientReports(startDate, endDate, practiceId = null) {
        let httpString =
          `${this.apiUrl}?r=report/get-client-reports` +
          `&start_date=${startDate}` +
          `&end_date=${endDate}`
        if (practiceId) {
            httpString += `&practice_id=${practiceId}`
        }
        return Vue.http.get(httpString).then((response) => response.data)
    },

    /**
     * A multi-purpose method for retrieving resources from the Yii 2 api - this can be used to fetch
     * a single resource or a list; the filters and listParams can be bound to a table for
     * paging/sorting/filtering support.
     *
     * @param table The Yii model table name (in snake case - e.g. 'plan_resource')
     * @param single Whether to fetch a single resource or a list
     * @param expands Optional related records to fetch as part of the request
     * @param filters Filters on model fields (use an 'id' filter to fetch a single resource)
     * @param listParams Paging/sorting options, bound to a table
     */
    async getResources(vue,
                       table,
                       single,
                       expands = [],
                       filters = undefined,
                       listParams = undefined) {
        let params = {r: table}
        if (expands && expands.length) {
            params.expand = expands.join(',')
        }
        if (listParams) {
            params.perPage = listParams.perPage
            params.page = listParams.currentPage - 1
            params.sort = (listParams.sortDesc ? '-' : '') + listParams.sortBy
        }
        if (filters) {
            params.filters = _.cloneDeep(filters)
            // prune the <select> options from each filter, if present
            // - as these can get really long and lead to a Request-URI Too Long error
            // (they aren't needed on the server anyway)
            _.each(params.filters, (f) => {
                f.options = []
            })
        }
        if (single) {
            params.perPage = 1
        }

        try {
            const response = await Vue.http.get(this.apiUrl, {
                params: params
            })
            if (listParams) {
                listParams.totalRows = response.body._meta.totalCount
            }
            if (single) {
                return (response.body.items[0])
            } else {
                return (response.body.items)
            }
        } catch (e) {
            if (vue) {
                Utils.handleError(e, 'Error loading data')
            }
        }
    }
}
